import { Head, Link } from "@inertiajs/react"
import { GraduationCap, ArrowRight } from "lucide-react"

import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import MarketingLayout from "@/layouts/marketing-layout"
import type { TeachersIndexProps, PublicTeacher } from "@/types/teacher"

export default function TeachersIndex({ teachers }: TeachersIndexProps) {
  return (
    <MarketingLayout>
      <Head title="Thai Teachers | ThaiCopilot">
        <meta
          name="description"
          content="Browse verified Thai teachers on ThaiCopilot. Find experienced, credible teachers to help you learn Thai through conversation practice and structured lessons."
        />
      </Head>

      {/* Hero */}
      <div className="border-b bg-gradient-to-br from-indigo-50 via-white to-violet-50/50 dark:from-slate-900 dark:via-slate-950 dark:to-indigo-950/20">
        <div className="container mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16 lg:px-8">
          <div className="max-w-3xl">
            <Badge className="mb-4 gap-1.5 bg-indigo-100 text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-300">
              <GraduationCap className="size-3.5" />
              Verified Teachers
            </Badge>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl dark:text-white">
              Learn from Verified Thai Teachers
            </h1>
            <p className="mt-4 text-lg text-slate-600 dark:text-slate-300">
              Every teacher on ThaiCopilot is manually verified. Browse profiles
              and find the right teacher for your learning goals.
            </p>
          </div>
        </div>
      </div>

      {/* Teacher Grid */}
      <div className="min-h-[60vh] bg-white dark:bg-slate-950">
        <div className="container mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:px-8">
          {teachers.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {teachers.map((teacher) => (
                <TeacherCard key={teacher.id} teacher={teacher} />
              ))}
            </div>
          )}
        </div>
      </div>
    </MarketingLayout>
  )
}

function TeacherCard({ teacher }: { teacher: PublicTeacher }) {
  return (
    <Link
      href={`/teachers/${teacher.slug}`}
      className="group flex flex-col rounded-xl border border-slate-200 bg-white p-6 transition-all duration-200 hover:-translate-y-1 hover:border-indigo-200 hover:shadow-lg hover:shadow-indigo-500/10 dark:border-slate-800 dark:bg-slate-900 dark:hover:border-indigo-800 dark:hover:shadow-indigo-500/5"
    >
      {/* Avatar + Name */}
      <div className="flex items-center gap-4">
        <div className="rounded-full bg-gradient-to-br from-indigo-400 to-violet-500 p-[2px]">
          <Avatar className="size-12 border-2 border-white dark:border-slate-900">
            <AvatarFallback className="bg-indigo-100 text-sm font-semibold text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300">
              {teacher.avatarInitials}
            </AvatarFallback>
          </Avatar>
        </div>
        <div className="min-w-0">
          <h3 className="truncate text-base font-semibold text-slate-900 dark:text-white">
            {teacher.name}
          </h3>
          <div className="flex items-center gap-1.5 text-sm text-indigo-600 dark:text-indigo-400">
            <GraduationCap className="size-3.5" />
            <span>Verified Teacher</span>
          </div>
        </div>
      </div>

      {/* Bio preview */}
      {teacher.bio && (
        <p className="mt-4 line-clamp-3 text-sm leading-relaxed text-slate-600 dark:text-slate-400">
          {teacher.bio}
        </p>
      )}

      {/* Experience snippet */}
      {teacher.teachingExperience && (
        <p className="mt-3 line-clamp-1 text-xs text-slate-400 dark:text-slate-500">
          {teacher.teachingExperience}
        </p>
      )}

      {/* View profile link */}
      <div className="mt-auto pt-4">
        <span className="inline-flex items-center gap-1 text-sm font-medium text-indigo-600 transition-colors group-hover:text-indigo-700 dark:text-indigo-400 dark:group-hover:text-indigo-300">
          View Profile
          <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-0.5" />
        </span>
      </div>
    </Link>
  )
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center py-20 text-center">
      <div className="mb-4 flex size-16 items-center justify-center rounded-2xl bg-slate-100 dark:bg-slate-800">
        <GraduationCap className="size-8 text-slate-400 dark:text-slate-500" />
      </div>
      <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
        Teachers Coming Soon
      </h2>
      <p className="mt-2 max-w-md text-base text-slate-500 dark:text-slate-400">
        We're reviewing applications from qualified Thai teachers. Check back
        soon to find the perfect teacher for your learning journey.
      </p>
      <Link
        href="/teach-thai"
        className="mt-6 text-sm font-medium text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300"
      >
        Are you a teacher? Apply here
      </Link>
    </div>
  )
}
